import React from "react";
import PropTypes from "prop-types";
import { TextField, MenuItem } from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

/**
 * PremiumSelect - A reusable select dropdown with premium styling
 * @param {Object} props
 * @param {string} props.label - Label for the select
 * @param {string} props.value - Current value
 * @param {Array} props.options - Array of options
 * @param {function} props.onChange - Change handler
 * @param {boolean} props.required - Is field required
 */
const PremiumSelect = ({
  label,
  value,
  options = [],
  onChange,
  required = false,
  placeholder = "All",
  ...rest
}) => {
  const renderValue = (selected) => {
    if (selected === "" || selected === undefined || selected === null) {
      return <span style={{ color: "#94a3b8" }}>{placeholder}</span>;
    }
    return selected;
  };

  return (
    <ArgonBox mb={2}>
      <ArgonTypography
        variant="caption"
        fontWeight="bold"
        sx={{
          color: "#64748b",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          mb: 1,
          display: "block",
        }}
      >
        {label} {required && <span style={{ color: "#ef5350" }}>*</span>}
      </ArgonTypography>
      <TextField
        select
        fullWidth
        value={value ?? ""}
        onChange={onChange}
        variant="outlined"
        SelectProps={{
          displayEmpty: true,
          renderValue,
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "12px",
            backgroundColor: "#f8fafc",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "#f1f5f9",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#667eea",
                borderWidth: "2px",
              },
            },
            "&.Mui-focused": {
              backgroundColor: "#fff",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#667eea",
                borderWidth: "2px",
                boxShadow: "0 0 0 4px rgba(102, 126, 234, 0.1)",
              },
            },
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#e2e8f0",
            transition: "all 0.3s ease",
          },
        }}
        {...rest}
      >
        {options.map((option) => {
          const isEmpty = option === "" || option === null || option === undefined;
          const optionKey = isEmpty ? "__empty" : option;
          const optionLabel = isEmpty ? placeholder : option;

          return (
            <MenuItem key={optionKey} value={option}>
              {optionLabel}
            </MenuItem>
          );
        })}
      </TextField>
    </ArgonBox>
  );
};

PremiumSelect.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
};

PremiumSelect.defaultProps = {
  value: "",
  required: false,
  placeholder: "All",
};

export default PremiumSelect;
